import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	signal,
	OnDestroy,
	viewChild,
} from '@angular/core';
import {
	ChartType,
	NumberChartConfig,
} from '../../../models/chart-config.model';
import { DataService } from '../../../services/data.service';
import { PaletteService } from '../../../services/palette.service';
import { ThemeMode, ThemeService } from '../../../services/theme.service';
import { resolveChartThemeMode } from '../../../utils/resolve-chart-theme-mode.util';
import Highcharts from '../highcharts.config';
import { parseChartVisualizationInput } from '../../../utils/parse-chart-visualization-input.util';
import { numberChartSparklineDefaults } from './jepto-number-chart.defaults';
import { resolveNumberSparklineLineWidth } from './jepto-number-chart-editor-options';
import { resolveNumberFractionDigits } from './jepto-number-chart-format.util';
import { safeHighchartsReflow } from '../../../utils/resolve-highcharts-css-colors.util';
import {
	COMPARISON_NEW_LABEL,
	COMPARISON_NO_DATA_LABEL,
	derivePreviousFromPercentChange,
	formatComparisonPercentInParens,
	resolveComparisonDisplayState,
} from '../../../utils/comparison-display-format.util';

/** Accepts full number-chart payloads or a partial WS/editor payload. */
type NumberChartInputData = {
	chartType?: ChartType | string;
	data?: {
		value?: number;
		format?: {
			prefix?: string;
			suffix?: string;
			precision?: number;
		};
		sparklineData?: [string, number][];
		comparisonData?: {
			value?: number;
			unit?: string;
		};
		valueDetail?: string;
	};
};

@Component({
	selector: 'jepto-number-chart',
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './jepto-number-chart.component.html',
	styleUrl: './jepto-number-chart.component.scss',
})
export class JeptoNumberChartComponent implements AfterViewInit, OnDestroy {
	cardId = input<string>();
	config = input<NumberChartConfig>();
	data = input<NumberChartInputData>();
	themeMode = input<ThemeMode>('auto');
	palette = input<string>('pal_jepto');

	private sparklineContainer = viewChild<ElementRef>('sparklineContainer');

	private paletteService = inject(PaletteService);
	private themeService = inject(ThemeService);
	private dataService = inject(DataService, { optional: true });

	private sparklineChart?: Highcharts.Chart;
	private resizeObserver?: ResizeObserver;
	private initialized = signal(false);

	private readonly FALLBACK_SPARKLINE_COLOR = '#651FFF';

	private effectiveData = computed<NumberChartInputData | null>(() => {
		const direct = this.data();
		if (direct) return direct;

		const id = this.cardId();
		if (id && this.dataService) {
			const serviceData = this.dataService.cardDataSignal(id)();
			if (serviceData?.chartType === ChartType.Number) {
				const rawValue = (serviceData as { data?: { value?: unknown } })
					.data?.value;
				if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
					return serviceData as unknown as NumberChartInputData;
				}
			}
		}

		return null;
	});

	protected value = computed(() => this.effectiveData()?.data?.value);

	protected valueDetail = computed(
		() => this.effectiveData()?.data?.valueDetail?.trim() ?? ''
	);

	protected metricLabel = computed(() => {
		const firstMetric = this.config()?.data?.metrics?.[0];
		return firstMetric?.label || firstMetric?.name || '';
	});

	/**
	 * Chart tab settings override the metric defaults in `data.format`, but only
	 * when set: the editor persists blank prefix/suffix and precision 0 (auto).
	 */
	protected prefix = computed(
		() =>
			this.config()?.presentation?.number?.format?.prefix ||
			this.effectiveData()?.data?.format?.prefix ||
			''
	);

	protected suffix = computed(
		() =>
			this.config()?.presentation?.number?.format?.suffix ||
			this.effectiveData()?.data?.format?.suffix ||
			''
	);

	private precision = computed(
		() =>
			this.config()?.presentation?.number?.format?.precision ||
			this.effectiveData()?.data?.format?.precision ||
			0
	);

	private compact = computed(
		() => this.config()?.presentation?.number?.format?.compact ?? false
	);

	protected valueSizeClass = computed(() => {
		const valueSize =
			this.config()?.presentation?.number?.valueSize ?? 'lg';
		return `jepto-number-chart__value--${valueSize}`;
	});

	protected formattedValue = computed(() => {
		const val = this.value();
		if (val === undefined || val === null || Number.isNaN(val)) {
			return '0';
		}

		const compact = this.compact();
		const { minimumFractionDigits, maximumFractionDigits } =
			resolveNumberFractionDigits(this.precision(), { compact });

		if (compact) {
			return new Intl.NumberFormat('en-US', {
				notation: 'compact',
				minimumFractionDigits,
				maximumFractionDigits,
			}).format(val);
		}

		return new Intl.NumberFormat('en-US', {
			minimumFractionDigits,
			maximumFractionDigits,
		}).format(val);
	});

	private comparisonData = computed(
		() => this.effectiveData()?.data?.comparisonData
	);

	protected changePercent = computed(() => this.comparisonData()?.value);

	protected hasComparison = computed(
		() => this.changePercent() !== undefined
	);

	private comparisonState = computed(() => {
		if (!this.hasComparison()) {
			return null;
		}
		const current = this.value();
		const delta = this.changePercent();
		const currentNumber =
			current === undefined || current === null || Number.isNaN(current)
				? null
				: current;
		let previous: number | null = null;
		if (
			currentNumber != null &&
			delta !== undefined &&
			Number.isFinite(delta)
		) {
			previous = derivePreviousFromPercentChange(currentNumber, delta);
		}
		return resolveComparisonDisplayState(currentNumber, previous);
	});

	protected changeDirection = computed<'up' | 'down' | 'flat'>(() => {
		const state = this.comparisonState();
		if (
			state == null ||
			state.kind === 'new' ||
			state.kind === 'no-data' ||
			state.percentChange == null ||
			state.percentChange === 0
		) {
			return 'flat';
		}
		return state.percentChange > 0 ? 'up' : 'down';
	});

	protected formattedPreviousValue = computed(() => {
		const state = this.comparisonState();
		if (state == null || state.kind === 'new' || state.kind === 'no-data') {
			return '';
		}
		const displayPrevious =
			this.precision() === 0
				? Math.round(state.displayPrevious)
				: state.displayPrevious;
		return this.formatMetricValue(displayPrevious);
	});

	protected formattedChangePercent = computed(() => {
		const state = this.comparisonState();
		if (state?.percentChange == null) {
			return '';
		}
		return formatComparisonPercentInParens(state.percentChange, 0);
	});

	protected comparisonPillText = computed(() => {
		const state = this.comparisonState();
		if (state == null) {
			return '';
		}
		if (state.kind === 'new') {
			return `0 (${COMPARISON_NEW_LABEL})`;
		}
		if (state.kind === 'no-data') {
			return `0 (${COMPARISON_NO_DATA_LABEL})`;
		}
		return `${this.formattedPreviousValue()} ${this.formattedChangePercent()}`.trim();
	});

	private formatMetricValue(value: number): string {
		const compact = this.compact();
		const { minimumFractionDigits, maximumFractionDigits } =
			resolveNumberFractionDigits(this.precision(), { compact });

		const formatted = compact
			? new Intl.NumberFormat('en-US', {
					notation: 'compact',
					minimumFractionDigits,
					maximumFractionDigits,
				}).format(value)
			: new Intl.NumberFormat('en-US', {
					minimumFractionDigits,
					maximumFractionDigits,
				}).format(value);

		return `${this.prefix()}${formatted}${this.suffix()}`;
	}

	private sparklineData = computed<[string, number][]>(
		() => this.effectiveData()?.data?.sparklineData ?? []
	);

	/** Match Chart-tab “Show sparkline” — do not infer from payload alone. */
	protected showSparkline = computed(
		() => this.config()?.presentation?.number?.showSparkline === true
	);

	protected sparklineHeight = computed(
		() => this.config()?.presentation?.number?.sparklineHeight ?? 60
	);

	private sparklineColor = computed(() => {
		const resolvedMode = resolveChartThemeMode(
			this.themeMode(),
			this.themeService.getActiveThemeModeSignal()()
		);
		const colors = this.paletteService.getColorPalette(
			this.palette(),
			resolvedMode
		);
		return colors[0] ?? this.FALLBACK_SPARKLINE_COLOR;
	});

	private sparklineSeriesData = computed<number[]>(() =>
		this.sparklineData().map(([, value]) => value)
	);

	private sparklineLineWidth = computed(() => {
		const cfg = this.config();
		const viz = parseChartVisualizationInput(cfg?.visualization) as
			| Highcharts.Options
			| undefined;
		return resolveNumberSparklineLineWidth(cfg?.presentation?.number, viz);
	});

	private sparklineOptions = computed<Highcharts.Options>(() => {
		const seriesData = this.sparklineSeriesData();
		const height = this.sparklineHeight();
		const color = this.sparklineColor();
		const lineWidth = this.sparklineLineWidth();

		return Highcharts.merge(numberChartSparklineDefaults, {
			chart: { height },
			plotOptions: {
				series: { lineWidth },
			},
			series: [
				{
					type: 'line',
					data: seriesData,
					color,
				},
			],
		} as Highcharts.Options);
	});

	constructor() {
		effect(() => {
			const isInitialized = this.initialized();
			const shouldShow = this.showSparkline();
			const seriesData = this.sparklineSeriesData();
			const options = this.sparklineOptions();

			if (!isInitialized) return;

			const container = this.sparklineContainer();
			if (!container || !shouldShow || seriesData.length === 0) {
				this.sparklineChart?.destroy();
				this.sparklineChart = undefined;
				return;
			}

			this.sparklineChart?.destroy();
			this.sparklineChart = Highcharts.chart(
				container.nativeElement,
				options
			);
		});
	}

	ngAfterViewInit(): void {
		this.initialized.set(true);
		this.setupResizeObserver();
	}

	ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
		this.sparklineChart?.destroy();
	}

	private setupResizeObserver(): void {
		if (typeof ResizeObserver === 'undefined') return;

		const container = this.sparklineContainer();
		if (!container) return;

		this.resizeObserver = new ResizeObserver(() => {
			setTimeout(() => {
				safeHighchartsReflow(this.sparklineChart);
			}, 0);
		});
		this.resizeObserver.observe(container.nativeElement);
	}
}
